// =============================================================================
// moduleTrial — wspólny słownik odmów `module_trial_start` (migracja 467).
//
// Trial modułu zakłada WYŁĄCZNIE serwer: migracja `467` odebrała najemcy zapis
// do `module_subscriptions` (polityki INSERT/UPDATE/DELETE tylko dla
// `is_super_admin()`) i wystawiła w zamian dwie wąskie funkcje `SECURITY
// DEFINER`. `module_trial_start` sama ustala `status`, `plan` i datę końca, a
// wołający nie podaje żadnego z nich.
//
// Powodów odmowy jest kilka i tylko funkcja w bazie zna regułę, więc sygnalizuje
// je rozpoznawalnym prefiksem w `RAISE EXCEPTION`. Ten plik jest jedynym
// miejscem, w którym prefiks zamienia się w zdanie dla człowieka — bo woła tę
// funkcję już DRUGI ekran (panel modułów i akt otwarcia Genesis), a dwie kopie
// tłumaczenia rozjechałyby się przy pierwszej zmianie komunikatu.
// =============================================================================

// -----------------------------------------------------------------------------
// Odmowy z GOTOWYM ZDANIEM w `HINT` (migracja 754: minimalny plan modułu
// i licencja ilościowa — oba z wyzwalaczy na `module_subscriptions`, więc
// przychodzą z KAŻDEJ funkcji, która zapisuje moduł). Zdanie układa baza, bo
// tylko ona zna nazwę modułu, wymagany plan i plan organizacji — panel go nie
// składa drugi raz. Zapasowe zdanie jest na wypadek starszego serwera bez
// `HINT`. Odmowy samego „Włącz/Wyłącz" (`module_subscription_set_enabled`)
// należą do migracji naprawy kasy i mają tam własny słownik.
// -----------------------------------------------------------------------------

export const ODMOWY_Z_PODPOWIEDZIA = {
  module_requires_plan:
    'Ten moduł wymaga wyższego planu Universe. Podnieś plan albo skontaktuj się z opiekunem.',
  licencja_ilosciowa:
    'Licencją tego modułu zarządza opiekun — w panelu nie uruchamia się jej ani nie wyłącza.',
} as const;

export type OdmowaZPodpowiedzia = keyof typeof ODMOWY_Z_PODPOWIEDZIA;

/**
 * Kod odmowy na POCZĄTKU komunikatu (`kod` albo `kod: <moduł>`), nie gdziekolwiek
 * w nim — kod przedłużony (`module_requires_plan_x`) ani wpleciony w zdanie
 * nie jest tym kodem.
 */
export function kodOdmowy(komunikat: string | null | undefined): OdmowaZPodpowiedzia | null {
  if (!komunikat) return null;
  for (const kod of Object.keys(ODMOWY_Z_PODPOWIEDZIA) as OdmowaZPodpowiedzia[]) {
    if (komunikat === kod || komunikat.startsWith(`${kod}:`)) return kod;
  }
  return null;
}

function zdanieZPodpowiedzi(komunikat: string, hint?: string | null): string | null {
  const kod = kodOdmowy(komunikat);
  if (!kod) return null;
  const podpowiedz = hint?.trim();
  return podpowiedz ? podpowiedz : ODMOWY_Z_PODPOWIEDZIA[kod];
}

/**
 * Zdanie dla człowieka po odmowie `module_subscription_set_enabled`. Odmowa
 * znana → zdanie z bazy; każda inna → `zapasowe` (dotychczasowe zachowanie
 * ekranu), bo surowy komunikat Postgresa nie jest dla administratora klienta.
 */
export function bladPrzelaczeniaModulu(
  blad: { message?: string | null; hint?: string | null } | null | undefined,
  zapasowe: string
): string {
  return zdanieZPodpowiedzi(blad?.message ?? '', blad?.hint) ?? zapasowe;
}

/** Przetłumacz odmowę z `module_trial_start` na zdanie dla człowieka. */
export function bladTriala(komunikat: string, moduleId: string, hint?: string | null): string {
  const zBazy = zdanieZPodpowiedzi(komunikat, hint);
  if (zBazy) return zBazy;
  if (komunikat.includes('trial_already_used')) {
    return 'Wersja próbna tego modułu była już wykorzystana. Wybierz pakiet, aby korzystać dalej.';
  }
  if (komunikat.includes('module_already_active')) {
    return 'Ten moduł już działa albo jest opłacony — wersja próbna nie jest potrzebna.';
  }
  if (komunikat.includes('module_suspended')) {
    return 'Ten moduł jest wyłączony — wersja próbna by go nie przywróciła. Użyj przycisku „Włącz ponownie" na karcie modułu.';
  }
  if (komunikat.includes('module_not_available')) {
    return 'Ten moduł nie jest obecnie dostępny do samodzielnego uruchomienia.';
  }
  if (komunikat.includes('module_not_found')) {
    return `Moduł „${moduleId}" nie jest zarejestrowany w bazie. Skontaktuj się z administratorem.`;
  }
  if (komunikat.includes('forbidden')) {
    return 'Tylko właściciel lub administrator organizacji może uruchomić wersję próbną.';
  }
  // Nieznany komunikat wraca surowy: lepszy nieokrzesany błąd niż zmyślony.
  return komunikat;
}

/** Zdanie prowadzące do przycisku zakupu, który karta wyłączonego modułu pokazuje. */
const DO_ZAKUPU = 'Użyj przycisku „Kup subskrypcję" na karcie modułu.';

/**
 * Przetłumacz odmowę z `module_subscription_set_enabled` (migracja naprawy kasy,
 * 02.10.2026) na zdanie dla człowieka. Włączyć da się wyłącznie moduł wyłączony
 * przez organizację, opłacony i przed końcem dostępu. Każde zdanie prowadzi do
 * przycisku, który na tej karcie naprawdę jest (`akcjaWylaczonegoModulu`).
 */
export function bladWlaczenia(komunikat: string): string {
  if (komunikat.includes('subscription_closed_in_stripe')) {
    return `Subskrypcja tego modułu nie jest już opłacona. ${DO_ZAKUPU}`;
  }
  if (komunikat.includes('subscription_expired')) {
    return `Okres dostępu do tego modułu minął. ${DO_ZAKUPU}`;
  }
  if (komunikat.includes('module_suspended_by_operator')) {
    return 'Ten moduł zawiesił operator platformy — włączy go tylko on. Skontaktuj się z obsługą.';
  }
  if (komunikat.includes('module_not_suspended')) {
    return 'Tego modułu nie wyłączała organizacja — nie da się go włączyć tym przyciskiem.';
  }
  if (komunikat.includes('forbidden')) {
    return 'Tylko właściciel lub administrator organizacji może włączać moduły.';
  }
  return 'Nie udało się włączyć modułu';
}

/** Wiersz `module_subscriptions` w zakresie, który czyta karta wyłączonego modułu. */
export interface WylaczonyModul {
  status: string;
  suspended_by?: string | null;
  subscription_ends_at?: string | null;
  stripe_subscription_id?: string | null;
  stripe_status?: string | null;
}

const ZYWE_STRIPE = ['active', 'trialing', 'past_due'];

/**
 * Co karta WYŁĄCZONEGO modułu ma zaproponować — ta sama reguła, co
 * `module_subscription_set_enabled`, żeby panel nie pokazywał przycisku, któremu
 * serwer odmówi:
 *   * `operator` — zawiesił super-admin (albo nie wiadomo kto): tylko kontakt;
 *   * `kup` — dostęp się skończył albo subskrypcja Stripe nie jest opłacona;
 *   * `wlacz` — najemca wyłączył sam, dostęp trwa.
 */
export function akcjaWylaczonegoModulu(
  sub: WylaczonyModul,
  teraz: Date = new Date()
): 'operator' | 'kup' | 'wlacz' {
  if (sub.suspended_by !== 'tenant') return 'operator';
  if (sub.stripe_subscription_id && !ZYWE_STRIPE.includes(sub.stripe_status ?? '')) return 'kup';
  if (sub.subscription_ends_at && Date.parse(sub.subscription_ends_at) <= teraz.getTime()) {
    return 'kup';
  }
  return 'wlacz';
}
